import { useState } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { authStore } from "@/store/auth"
import { AlertCircle, CreditCard, Loader2, QrCode, Receipt, Shield } from "lucide-react"
import { toast } from "sonner"

import type { CreditCardFormData } from "../../schemas"
import { pagarmeService } from "../../services/pagarme-service"
import { useBillingStore } from "../../store/billing-store"
import type {
  PagarmePaymentMethod,
  PagarmePixOrderResponse,
  PagarmeSubscriptionResponse,
  Plan,
} from "../../types"
import { BoletoPayment } from "./boleto-payment"
import { CreditCardForm } from "./credit-card-form"
import { PixPayment } from "./pix-payment"
import { PlanSummary } from "./plan-summary"

function formatCpfCnpj(value: string): string {
  const digits = value.replace(/\D/g, "")
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
  }
  return digits
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2")
}

interface PagarmeCheckoutFormProps {
  selectedPlan: Plan
  onPaymentSuccess?: () => void
  onPaymentError?: (error: string) => void
}

export function PagarmeCheckoutForm({
  selectedPlan,
  onPaymentSuccess,
  onPaymentError,
}: PagarmeCheckoutFormProps) {
  const [paymentMethod, setPaymentMethod] = useState<PagarmePaymentMethod>("credit_card")
  const [installments, setInstallments] = useState(1)
  const [paymentResult, setPaymentResult] = useState<PagarmeSubscriptionResponse | null>(null)
  const [pixResult, setPixResult] = useState<PagarmePixOrderResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [customerDocument, setCustomerDocument] = useState("")
  const [documentError, setDocumentError] = useState<string | null>(null)

  const { isLoading, createPagarmeSubscription } = useBillingStore()
  const user = authStore(s => s.user)

  const validateDocument = (): boolean => {
    const digits = customerDocument.replace(/\D/g, "")
    if (!digits || (digits.length !== 11 && digits.length !== 14)) {
      setDocumentError("Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido")
      return false
    }
    setDocumentError(null)
    return true
  }

  const pagarmePublicKey = import.meta.env.VITE_PAGARME_PUBLIC_KEY

  const handleCreditCardSubmit = async (cardData: CreditCardFormData) => {
    setError(null)

    if (!validateDocument()) return

    try {
      if (!pagarmePublicKey) {
        throw new Error("Pagar.me public key not configured")
      }

      // Tokenize card client-side
      const cardToken = await pagarmeService.tokenizeCard(pagarmePublicKey, {
        number: cardData.number,
        holder_name: cardData.holderName,
        exp_month: parseInt(cardData.expMonth),
        exp_year: parseInt(cardData.expYear),
        cvv: cardData.cvv,
      })

      // Create subscription
      const result = await createPagarmeSubscription({
        plan_id: selectedPlan.id,
        payment_method: "credit_card",
        card_token: cardToken,
        installments,
        customer_name: user?.name || "",
        customer_email: user?.email || "",
        customer_document: customerDocument.replace(/\D/g, ""),
      })

      setPaymentResult(result)

      if (result.status === "active") {
        toast.success("Assinatura criada com sucesso!")
        onPaymentSuccess?.()
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao processar pagamento"
      setError(message)
      onPaymentError?.(message)
    }
  }

  const handleBoletoSubmit = async () => {
    setError(null)

    if (!validateDocument()) return

    try {
      const result = await createPagarmeSubscription({
        plan_id: selectedPlan.id,
        payment_method: "boleto",
        customer_name: user?.name || "",
        customer_email: user?.email || "",
        customer_document: customerDocument.replace(/\D/g, ""),
      })

      if (result.status === "failed") {
        setError("Pagamento recusado. Tente outro método de pagamento.")
        onPaymentError?.("Pagamento recusado")
        return
      }
      setPaymentResult(result)
      toast.info("Boleto gerado! Pague até a data de vencimento.")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao processar pagamento"
      setError(message)
      onPaymentError?.(message)
    }
  }

  const handlePixSubmit = async () => {
    setError(null)

    if (!validateDocument()) return

    try {
      const result = await pagarmeService.createPixOrder({
        plan_id: selectedPlan.id,
        customer_name: user?.name || "",
        customer_email: user?.email || "",
        customer_document: customerDocument.replace(/\D/g, ""),
      })

      setPixResult(result)
      toast.info("Código PIX gerado! Escaneie o QR Code para pagar.")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao processar pagamento"
      setError(message)
      onPaymentError?.(message)
    }
  }

  const handleCheckPixStatus = async (): Promise<boolean> => {
    if (!pixResult?.order_id) return false
    try {
      const status = await pagarmeService.checkPixOrderStatus(pixResult.order_id)
      return status.status === "active"
    } catch {
      return false
    }
  }

  // If PIX order result, show QR code with polling
  if (pixResult?.pix_qr_code || pixResult?.pix_qr_code_url) {
    return (
      <div className="grid gap-6 md:grid-cols-2">
        <PlanSummary selectedPlan={selectedPlan} />
        <div className="p-8">
          <PixPayment
            qrCode={pixResult.pix_qr_code}
            qrCodeUrl={pixResult.pix_qr_code_url}
            expiresAt={pixResult.pix_expires_at}
            onPaymentConfirmed={onPaymentSuccess}
            onCheckStatus={handleCheckPixStatus}
          />
        </div>
      </div>
    )
  }

  if (paymentResult?.boleto_url || paymentResult?.boleto_barcode) {
    return (
      <div className="grid gap-6 md:grid-cols-2">
        <PlanSummary selectedPlan={selectedPlan} />
        <div className="p-8">
          <BoletoPayment
            boletoUrl={paymentResult.boleto_url}
            boletoBarcode={paymentResult.boleto_barcode}
            boletoDueAt={paymentResult.boleto_due_at}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <PlanSummary selectedPlan={selectedPlan} />

      <div className="p-8">
        <h2 className="mb-1 text-xl font-semibold">Pagamento</h2>
        <p className="mb-6 text-sm text-gray-500">
          Escolha o método de pagamento para sua assinatura
        </p>

        <div className="mb-4 space-y-2">
          <Label htmlFor="customer-document">CPF ou CNPJ</Label>
          <Input
            id="customer-document"
            placeholder="000.000.000-00"
            maxLength={18}
            value={formatCpfCnpj(customerDocument)}
            onChange={e => {
              setCustomerDocument(e.target.value.replace(/\D/g, ""))
              setDocumentError(null)
            }}
          />
          {documentError && <p className="text-sm text-red-500">{documentError}</p>}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs
          value={paymentMethod}
          onValueChange={v => setPaymentMethod(v as PagarmePaymentMethod)}
        >
          <TabsList className="mb-6 grid w-full grid-cols-3">
            <TabsTrigger value="credit_card" className="gap-2">
              <CreditCard className="h-4 w-4" />
              Cartão
            </TabsTrigger>
            <TabsTrigger value="pix" className="gap-2">
              <QrCode className="h-4 w-4" />
              PIX
            </TabsTrigger>
            <TabsTrigger value="boleto" className="gap-2">
              <Receipt className="h-4 w-4" />
              Boleto
            </TabsTrigger>
          </TabsList>

          <TabsContent value="credit_card">
            <CreditCardForm
              onSubmit={handleCreditCardSubmit}
              isLoading={isLoading}
              showInstallments={selectedPlan.interval === "yearly"}
              maxInstallments={12}
              onInstallmentsChange={setInstallments}
            />
          </TabsContent>

          <TabsContent value="pix">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Ao clicar em &quot;Gerar PIX&quot;, um QR Code será exibido para pagamento
                instantâneo. A ativação é imediata após o pagamento.
              </p>
              <button
                className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium"
                onClick={handlePixSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Gerando PIX...
                  </>
                ) : (
                  "Gerar PIX"
                )}
              </button>
            </div>
          </TabsContent>

          <TabsContent value="boleto">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Um boleto será gerado com vencimento em 3 dias úteis. A ativação ocorre após a
                compensação (1-3 dias úteis).
              </p>
              <button
                className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium"
                onClick={handleBoletoSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Gerando boleto...
                  </>
                ) : (
                  "Gerar Boleto"
                )}
              </button>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-400">
          <Shield className="h-3 w-3" />
          <span>Pagamento seguro processado pela Pagar.me</span>
        </div>
      </div>
    </div>
  )
}
